<script setup>
import { ref } from 'vue'
import PortalDataTable from '@/components/DataTable/PortalDataTable.vue'
import { DataTableEndpointDefinition } from '@/models/datatable/DataTableEndpointDefinition.js'
import { Format } from '@/models/utils/FormatUtils.js'
import EndpointTableCellMenu from '@/components/Tables/EndpointTableCellMenu.vue'
import { Toaster } from '@/models/utils/Toaster.js'
import SubmitButton from '@/components/Forms/Buttons/SubmitButton.vue'
import { ButtonSize, ButtonType } from '@/components/Forms/Buttons/SubmitButtonConstants.js'
import { ModalButtonData } from '@/components/Alerts/ModalButtonData.js'
import MaintenanceEventModal from '@/views/Admin/SuperAdmin/MaintenanceEventModal.vue'
import { maintenanceApi } from '@/api/Support/MaintenanceApi.js'
import MultiButtonModal from '@/components/Alerts/MultiButtonModal.vue'
import { useBroadcastChannel } from '@vueuse/core'

const columns = [
	{ field: "created_at", title: "Created At", sort: true, width: '1%' },
	{ field: "start_at", title: "Scheduled Start", sort: true, width: '1%' },
	{ field: "end_at", title: "Scheduled End", sort: true, width: '1%' },
	{ field: "started_at", title: "Started At", sort: true, width: '1%' },
	{ field: "finished_at", title: "Finished At", sort: true, width: '1%' },
	{ field: "warning_minutes", title: "Warning (minutes)", sort: false, width: '1%' },
	{ field: "actions", title: "Actions", sort: false, width: '1%' }
]

const endpoints = [
	new DataTableEndpointDefinition({
		endpointRoute: 'maintenance-events',
		endpointDisplayName: 'Maintenance Events',
		initialSortColumn: 'id',
		initialSortDirection: 'desc',
		enableSearch: false,
		columns: columns,
	})
]

const table = ref(null)
const toaster = new Toaster()
const eventToEdit = ref(null)
const eventToDelete = ref(null)
const eventToStart = ref(null)
const eventToEnd = ref(null)

const channel = useBroadcastChannel({ name: 'MaintenancePanel' })

function getMenuItems(event)
{
	let items = [];
	let i = 1;

	items.push({ id: i++, text:'View', hidden: !event.finished_at })
	items.push({ id: i++, text:'Edit', hidden: event.finished_at })
	items.push({ id: i++, text:'Start', hidden: event.started_at })
	items.push({ id: i++, text:'End', hidden: !event.started_at || event.finished_at })
	items.push({ id: i++, text:'Delete' })

	return items;
}

function onMenuItemSelected(menuItem, record)
{
	switch (menuItem.text) {
		case "View":
			eventToEdit.value = record
			break;
		case "Edit":
			eventToEdit.value = record
			break;
		case "Start":
			eventToStart.value = record
			break;
		case "End":
			eventToEnd.value = record
			break;
		case "Delete":
			eventToDelete.value = record
			break;
		default:
			toaster.info('Invalid menu option.')
			break;
	}
}

function createEvent() {
	eventToEdit.value = {}
}

function deleteEvent(eventId) {
	maintenanceApi.deleteMaintenanceEvent(eventId, () => {
		eventToDelete.value = null
		toaster.success('Event successfully deleted')
		table.value.loadData()
	}, (e) => {
		eventToDelete.value = null
		toaster.error(e.showErrorBanner())
		table.value.loadData()
	})
}

function dismissEditModal() {
	eventToEdit.value = null
	table.value.loadData()
}

function onEventCreated() {
	eventToEdit.value = null
	toaster.success('Event successfully created')
	table.value.loadData()
	channel.post({})
}

function onEventUpdated() {
	eventToEdit.value = null
	toaster.success('Event successfully updated')
	table.value.loadData()
	channel.post({})
}

function startEvent(eventId) {
	maintenanceApi.startMaintenanceEvent(eventId, () => {
		if (eventToStart.value.warning_minutes == null || eventToStart.value.warning_minutes <= 0) {
			channel.post({ 'promptToReset': true })
		}
		else {
			toaster.success('Event successfully started')
			channel.post({})
		}

		eventToStart.value = null
		table.value.loadData()
	}, (e) => {
		eventToStart.value = null
		toaster.error(e.showErrorBanner())
	})
}

function endEvent(eventId) {
	maintenanceApi.endMaintenanceEvent(eventId, () => {
		eventToEnd.value = null
		toaster.success('Event successfully ended')
		table.value.loadData()
		channel.post({})
	}, (e) => {
		eventToEnd.value = null
		toaster.error(e.showErrorBanner())
	})
}

function getDeleteMessage(event) {
	if (!event) return

	let message = ''

	if (event.started_at && !event.finished_at) {
		message += '<b>Warning: This event is currently in progress.</b><br><br>'
	}

	message += 'Are you sure you want to delete this maintenance event?<br><br><b>This cannot be undone.</b>'

	return message
}

</script>

<template>
	<div class="flex flex-col">
		<SubmitButton class="ml-auto mt-2" label="Create Maintenance Event" @click="createEvent" :size="ButtonSize.ExtraSmall" />

		<PortalDataTable
			ref="table"
			:endpoints="endpoints"
		>
			<template #created_at="data">
				<span class="whitespace-nowrap">{{ Format.date(data.value.created_at) }}</span>
			</template>

			<template #start_at="data">
				<span class="whitespace-nowrap">{{ Format.date(data.value.start_at) }}</span>
			</template>

			<template #end_at="data">
				<span class="whitespace-nowrap">{{ Format.date(data.value.end_at) }}</span>
			</template>

			<template #started_at="data">
				<span class="whitespace-nowrap">{{ data.value.started_at ? Format.date(data.value.started_at) : '' }}</span>
			</template>

			<template #finished_at="data">
				<span class="whitespace-nowrap">{{ data.value.finished_at ? Format.date(data.value.finished_at) : '' }}</span>
			</template>

			<template #warning_minutes="data">
				{{ data.value.warning_minutes }}
			</template>

			<template #actions="data">
				<div class="flex w-full justify-center">
					<EndpointTableCellMenu
						:items="getMenuItems(data.value)"
						:record="data.value"
						:disabled="false"
						@onMenuItemSelected="onMenuItemSelected"
					/>
				</div>
			</template>
		</PortalDataTable>
	</div>

	<Teleport to="body">
		<MaintenanceEventModal
			v-if="eventToEdit"
			:event-to-edit="eventToEdit"
			@dismiss="dismissEditModal"
			@event-created="onEventCreated"
			@event-updated="onEventUpdated"
		/>
	</Teleport>

	<Teleport to="html">
		<MultiButtonModal
			:show="eventToDelete != null"
			icon="alert"
			title="Delete Maintenance Event"
			:message="getDeleteMessage(eventToDelete)"
			:buttons="[
				new ModalButtonData(
					'Cancel', () => { eventToDelete = null }, ButtonType.Default, ButtonSize.Small
				),
				new ModalButtonData(
					'Delete', () => { deleteEvent(eventToDelete.id) }, ButtonType.Default, ButtonSize.Small
				)
			]"
		/>
	</Teleport>

	<Teleport to="html">
		<MultiButtonModal
			:show="eventToStart != null"
			icon="info"
			title="Start Maintenance Event"
			message="Are you sure?"
			:buttons="[
				new ModalButtonData(
					'Cancel', () => { eventToStart = null }, ButtonType.Default, ButtonSize.Small
				),
				new ModalButtonData(
					'Start', () => { startEvent(eventToStart.id) }, ButtonType.Default, ButtonSize.Small
				)
			]"
		/>
	</Teleport>

	<Teleport to="html">
		<MultiButtonModal
			:show="eventToEnd != null"
			icon="info"
			title="End Maintenance Event"
			message="Are you sure?"
			:buttons="[
				new ModalButtonData(
					'Cancel', () => { eventToEnd = null }, ButtonType.Default, ButtonSize.Small
				),
				new ModalButtonData(
					'End', () => { endEvent(eventToEnd.id) }, ButtonType.Default, ButtonSize.Small
				)
			]"
		/>
	</Teleport>
</template>